<script setup lang="ts">
import { computed } from 'vue';
import { ICON_PIXEL_SIZES } from '@/components/document/sub-components/file-manager/views/file-explorer-view/iconSize';
import type { FileExplorerIconSize } from '@/components/document/sub-components/file-manager/logic/core/useFileManagerSettings';
import { HEADER_FONT_SIZES, META_FONT_SIZES, ROW_PADDING } from './detailsRowSizes';

const props = defineProps<{
  rowCount: number;
  iconSize: FileExplorerIconSize;
}>();

// Same density CSS custom properties DetailsTableView sets on its root, so a placeholder row's
// padding/font-size match the real row it's about to be replaced by exactly, at every icon-size
// tier — no reflow when the real rows swap in.
const sizeStyleVars = computed(() => ({
  '--details-row-padding': ROW_PADDING[props.iconSize],
  '--details-meta-font-size': META_FONT_SIZES[props.iconSize],
  '--details-header-font-size': HEADER_FONT_SIZES[props.iconSize],
}));

const iconPixelSize = computed(() => `${ICON_PIXEL_SIZES[props.iconSize]}px`);

// Deterministic (not Math.random()) width presets, cycled by row index, so bar widths look
// plausibly varied but never jitter across re-renders.
const NAME_WIDTHS = ['9rem', '13rem', '7rem', '11rem', '8.5rem'];
const MODIFIED_WIDTHS = ['4.5rem', '4rem', '5rem'];
const MODIFIED_BY_WIDTHS = ['5.5rem', '4rem', '6.5rem'];
const SIZE_WIDTHS = ['3rem', '2.25rem', '3.5rem'];

const rows = computed(() =>
  Array.from({ length: props.rowCount }, (_, index) => ({
    nameWidth: NAME_WIDTHS[index % NAME_WIDTHS.length],
    modifiedWidth: MODIFIED_WIDTHS[index % MODIFIED_WIDTHS.length],
    modifiedByWidth: MODIFIED_BY_WIDTHS[index % MODIFIED_BY_WIDTHS.length],
    sizeWidth: SIZE_WIDTHS[index % SIZE_WIDTHS.length],
  })),
);
</script>

<template>
  <div class="details-table-view" :style="sizeStyleVars">
    <div class="details-header">
      <span class="details-cell">Name</span>
      <span class="details-cell">Modified</span>
      <span class="details-cell">Modified by</span>
      <span class="details-cell details-size">Size</span>
    </div>
    <div v-for="(row, index) in rows" :key="index" class="details-row">
      <span class="details-cell skeleton-name">
        <span class="skeleton-icon" :style="{ width: iconPixelSize, height: iconPixelSize }" />
        <span class="skeleton-bar" :style="{ width: row.nameWidth }" />
      </span>
      <span class="details-cell">
        <span class="skeleton-bar" :style="{ width: row.modifiedWidth }" />
      </span>
      <span class="details-cell">
        <span class="skeleton-bar skeleton-pill" :style="{ width: row.modifiedByWidth }" />
      </span>
      <span class="details-cell details-size skeleton-cell-end">
        <span class="skeleton-bar" :style="{ width: row.sizeWidth }" />
      </span>
    </div>
  </div>
</template>

<style scoped>
/* Structural grid — copied verbatim from DetailsTableView.vue's .details-table-view /
   .details-header / .details-row / .details-cell rules so the skeleton lands on the exact same
   column tracks and row box model as the real table. Vue's scoped styles can't be shared across
   components without a new abstraction for this one small, stable block — keep this in sync with
   DetailsTableView.vue's grid if that ever changes. */
.details-table-view {
  display: grid;
  grid-template-columns: minmax(0, max-content) max-content max-content max-content;
  justify-content: space-between;
  column-gap: 1.5rem;
  row-gap: 0.125rem;
  padding: 0 2.5rem 0.5rem 1rem;
  min-width: max-content;
}

.details-header,
.details-row {
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
  align-items: center;
}

.details-header {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: var(--details-row-padding);
  background-color: #fff;
  border-bottom: 1px solid #e5e5e5;
  font-size: var(--details-header-font-size);
  font-weight: 600;
  color: #6b7280;
}

.details-row {
  padding: var(--details-row-padding);
  border-radius: 0.375rem;
}

.details-cell {
  min-width: 0;
  overflow: hidden;
}

.details-size {
  text-align: right;
}

.skeleton-cell-end {
  display: flex;
  justify-content: flex-end;
}

.skeleton-name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.skeleton-icon {
  flex-shrink: 0;
  border-radius: 0.25rem;
  background-color: #eceef1;
  animation: skeleton-pulse 1.4s ease-in-out infinite;
}

.skeleton-bar {
  display: inline-block;
  height: var(--details-meta-font-size);
  border-radius: 0.25rem;
  background-color: #eceef1;
  animation: skeleton-pulse 1.4s ease-in-out infinite;
}

.skeleton-pill {
  border-radius: 9999px;
}

@keyframes skeleton-pulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}
</style>
